import { z } from 'zod';
import { AttributeDataType, CustomAttributeKey, STANDARD_ATTRIBUTES } from '../catalog/attributes.ts';

/**
 * CSV user import (BB-36, DAT-013): upload → mapping → dry run (validate) → apply. Only attributes and subscription
 * groups; events go through the server API. A row is applied whole or not at all.
 */
export const IMPORT_MAX_BYTES = 100 * 1024 * 1024;
export const IMPORT_MAX_COLUMNS = 200;
/** Row errors kept per import; the rest are only counted. */
export const IMPORT_ERRORS_KEPT = 10_000;
/** Uploaded files are deleted after this many days; the error report stays. */
export const IMPORT_FILE_TTL_DAYS = 14;

export const IMPORT_STATUSES = ['uploaded', 'validating', 'validated', 'validation_failed', 'applying', 'completed', 'completed_with_errors', 'failed', 'cancelled'] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const IMPORT_ERROR_CODES = [
  'missing_identifier',
  'duplicate_identifier',
  'invalid_identifier',
  'invalid_value',
  'unsupported_type',
  'attribute_type_conflict',
  'attribute_blocked',
  'profile_not_found',
  'resubscribe_blocked',
  'subscription_group_not_found',
  'row_length_mismatch',
  'apply_failed',
] as const;
export type ImportErrorCode = (typeof IMPORT_ERROR_CODES)[number];

const STANDARD_KEYS = STANDARD_ATTRIBUTES.map((a) => a.key) as [string, ...string[]];

/** What one CSV column means. A new custom key needs its `dataType`; a known key uses the registry's type. */
export const ColumnMapping = z.discriminatedUnion('kind', [
  z.strictObject({ column: z.string().min(1).max(255), kind: z.literal('identifier'), field: z.enum(['external_id', 'alias_label', 'alias_name']) }),
  z.strictObject({ column: z.string().min(1).max(255), kind: z.literal('standard'), key: z.enum(STANDARD_KEYS) }),
  z.strictObject({ column: z.string().min(1).max(255), kind: z.literal('custom'), key: CustomAttributeKey, dataType: AttributeDataType.exclude(['object']).optional() }),
  /** `groupId` is the group's `api_id`; cells are `subscribed` or `unsubscribed`. */
  z.strictObject({ column: z.string().min(1).max(255), kind: z.literal('subscription_group'), groupId: z.uuid() }),
  z.strictObject({ column: z.string().min(1).max(255), kind: z.literal('ignore') }),
]);
export type ColumnMapping = z.infer<typeof ColumnMapping>;

export const ImportOptions = z
  .strictObject({
    /** Unknown identifiers create profiles; otherwise their rows fail with `profile_not_found`. */
    createMissing: z.boolean().default(true),
    /** Rows may subscribe users who unsubscribed explicitly (SUB-006); needs a reason for the audit. */
    allowResubscribe: z.boolean().default(false),
    resubscribeReason: z.string().trim().min(1).max(500).optional(),
  })
  .refine((o) => !o.allowResubscribe || Boolean(o.resubscribeReason), { message: 'a reason is required to re-subscribe users', path: ['resubscribeReason'] });
export type ImportOptions = z.infer<typeof ImportOptions>;

export const PutMappingBody = z.strictObject({ mapping: z.array(ColumnMapping).min(1).max(IMPORT_MAX_COLUMNS), options: ImportOptions });
export type PutMappingBody = z.infer<typeof PutMappingBody>;

export const UploadQuery = z.object({ fileName: z.string().trim().min(1).max(255) });
export const ImportListQuery = z.object({ cursor: z.string().max(200).optional(), limit: z.coerce.number().int().min(1).max(100).default(25) });

export interface ImportRowErrorView {
  rowNumber: number;
  column: string | null;
  code: ImportErrorCode;
  message: string;
  /** Personal-data columns show `[redacted]`. */
  valuePreview: string | null;
  phase: 'validate' | 'apply';
}

export interface ImportView {
  id: string;
  fileName: string;
  sizeBytes: number;
  status: ImportStatus;
  uploadedBy: { id: string; email: string | null } | null;
  headers: string[];
  mapping: ColumnMapping[] | null;
  options: ImportOptions;
  mappingVersion: number;
  /** The mapping version the dry run checked; apply needs it to equal `mappingVersion`. */
  validatedMappingVersion: number | null;
  totalRows: number | null;
  validRows: number | null;
  errorRows: number | null;
  newProfilesEstimate: number | null;
  rowsApplied: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  fileDeletedAt: string | null;
  /** The first 100 row errors (the full list is `errors.csv`). */
  errors: ImportRowErrorView[];
  /** A first mapping from the column names, while none is saved. */
  suggestedMapping?: ColumnMapping[];
}

export type ImportSummary = Omit<ImportView, 'headers' | 'mapping' | 'errors' | 'suggestedMapping'>;

export type UploadResult = ImportView & { suggestedMapping: ColumnMapping[] };
